import { useCallback } from "react";
import { SdbProject } from "@custom-types/project-types";
import { useOnUploadLsDataV2Files } from "@hooks/data-management/use-on-upload-files-ls-data-v2";
import { useOnUploadFocusFiles } from "@hooks/data-management/use-on-upload-files-focus";
import { useOnUploadPhotogrammetryFiles } from "@hooks/data-management/use-on-upload-files-photogrammetry";
import { UploadSettings } from "@hooks/data-management/use-upload-multiple-scans";
import {
  AnyFilesInfo,
  isBlinkFileInfo,
  isInvalidFileInfo,
  isFocusFileInfo,
  isPhotogrammetryFileInfo,
  isOrbisFileInfo,
} from "@pages/data-management/data-management-types";
import { assert } from "@faro-lotv/foundation";
import { StagingAreaApiClient } from "@api/stagingarea-api/stagingarea-api";


export type ReturnFunctionOnUploadFiles = (
  /**
   * Data provided by the associated file validation hook, e.g. useOnCheckFiles.
   * Allow info to be null for when we are retrying previous files.
   */
  fileInfos: AnyFilesInfo[] | null,
  uploadSettings: UploadSettings | undefined,
  shouldRetry?: boolean,
) => Promise<boolean>;

let prevFilesInfo: AnyFilesInfo[] | null = null;
let prevUploadSettings: UploadSettings | undefined = undefined;

/**
 * Hook for handling file uploads of the types:
 *  - Blink
 *  - Orbis
 *  - Focus
 *  - Photogrammetry
 * Returns true on success, false if some kind of error happened.
 * @param project The project to upload the files to.
 */
export function useOnUploadFiles(project: SdbProject): ReturnFunctionOnUploadFiles {
  const onUploadLsDataV2Files = useOnUploadLsDataV2Files(project);
  const onUploadFocusFiles = useOnUploadFocusFiles(project);
  const onUploadPhotogrammetryFiles = useOnUploadPhotogrammetryFiles(project);

  return useCallback(
    async (fileInfos, uploadSettings, shouldRetry = false): Promise<boolean> => {
      // if shouldRetry = true then fileInfos should be null, we will use the previous saved one
      assert((shouldRetry && fileInfos === null) || (fileInfos && fileInfos.length > 0 && !fileInfos.some(isInvalidFileInfo)));
      let isSuccess = true;

      if (shouldRetry) {
        if (!prevFilesInfo) {
          return false;
        }
        // Use stored references for retry
        fileInfos = prevFilesInfo;
        uploadSettings = prevUploadSettings;
      } else if (fileInfos) {
        prevFilesInfo = fileInfos;
        prevUploadSettings = uploadSettings;
      } else {
        return false;
      }

      // Photogrammetry files
      const photogrammetryFilesInfos = fileInfos.filter(isPhotogrammetryFileInfo);
      for (const fileInfo of photogrammetryFilesInfos) {
        isSuccess = isSuccess && (await onUploadPhotogrammetryFiles(fileInfo, uploadSettings));
      }

      // Orbis projects and single orbis files
      const orbisFilesInfos = fileInfos.filter(isOrbisFileInfo);
      if (orbisFilesInfos.length > 0) {
        const orbisFilesInfosRealMerged = StagingAreaApiClient.mergeScanFileInfos(orbisFilesInfos);
        isSuccess = isSuccess && (await onUploadLsDataV2Files(orbisFilesInfosRealMerged, uploadSettings));
      }

      // Blink projects with LsDataV2 metadata
      const blinkFilesInfos = fileInfos.filter(isBlinkFileInfo);
      if (blinkFilesInfos.length > 0) {
        const blinkFilesInfosMerged = StagingAreaApiClient.mergeScanFileInfos(blinkFilesInfos);
        isSuccess = isSuccess && (await onUploadLsDataV2Files(blinkFilesInfosMerged, uploadSettings));
      }

      // Focus projects with dummy LsDataV2
      const focusFilesInfos = fileInfos.filter(isFocusFileInfo);
      for (const fileInfo of focusFilesInfos) {
        isSuccess = isSuccess && (await onUploadFocusFiles(fileInfo, uploadSettings));
      }

      return isSuccess;
    },
    [onUploadLsDataV2Files, onUploadFocusFiles, onUploadPhotogrammetryFiles]
  );
}
